import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import DashboardPage from './pages/DashboardPage';
import UserContext from './UserContext';
import LoggedOut from './pages/LoggedOut';
import ErrorPage from './pages/ErrorPage';
import { Backdrop, CircularProgress } from '@mui/material';
import { getCurrentUser } from './rest/BackendApi';
import ManagePanel from './pages/ManagePanel';
import XeroSso from './XeroSso';
import AppConfig from './AppConfig';
import UserSettingsPage from './pages/UserSettingsPage';
import SubscriptionConfirmation from './pages/SubscriptionConfirmation';
import Subscription from './pages/Subscription';
import SideNav from './sidenav';
import './App.css';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import minMax from 'dayjs/plugin/minMax';
import OrganisationPage from './pages/OrganisationPage';
import SupportPage from './pages/SupportPage';
import FirmPage from './pages/FirmPage';
import Header from './Header';
import EmailsPage from './pages/EmailsPage';
import XpmPage from './pages/XpmPage';
import MessageContext, { MessageType } from './MessageContext';
import MessagePanel from './components/MessagePanel';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(minMax);
dayjs.tz.setDefault('GMT');

/** Pages that work without being logged in, so loading the user mustn't redirect away from them. */
const PUBLIC_PATHS = ['/subscription-confirmation', '/logged-out', '/login', '/signup', '/error', '/support'];

export const Application = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [message, setMessage] = useState<MessageType>({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Empty message when navigating away. Not when only the query string changes (e.g. the dashboard month),
    // as every message change re-renders the page.
    setMessage({});
  }, [location.pathname]);

  const loadAndSetCurrentUser = async (ignoreErrors: boolean) => {
    const currentUser = await getCurrentUser(ignoreErrors);
    if (currentUser) {
      setCurrentUser(currentUser);
      return currentUser;
    }
    return null;
  };

  // Pages that need the user render nothing until it has loaded
  const ifLoggedIn = (page: React.ReactElement) => (currentUser != null ? page : null);

  const userContext = { user: currentUser, refreshUser: loadAndSetCurrentUser };
  const messageContext = { message, setMessage };

  useEffect(() => {
    const loadCurrentUserAndRedirectIfNeeded = async () => {
      try {
        setPageLoading(true);
        const user = await loadAndSetCurrentUser(false);
        setPageLoading(false);
        if (user) {
          if (!user.hasActiveSubscription) {
            navigate('/subscription');
          }
        } else {
          navigate('/login');
        }
        // todo handle no current user if possible?
      } catch (error: any) {
        console.error('Error while fetching current user', error);
        window.location.href = `/error?status=${error.status}&message=${error.message}`;
      }
    };

    if (currentUser == null) {
      if (PUBLIC_PATHS.includes(window.location.pathname)) {
        loadAndSetCurrentUser(true);
      } else {
        loadCurrentUserAndRedirectIfNeeded();
      }
    }
  }, []);

  return (
    <div className="App">
      <MessageContext.Provider value={messageContext}>
        <UserContext.Provider value={userContext}>
          <SideNav />
          <main>
            <Header />
            <MessagePanel
              setErrorMessage={() => setMessage({})}
              errorMessage={message.type == 'error' ? message.content : null}
              setSuccessMessage={() => setMessage({})}
              successMessage={message.type == 'success' ? message.content : null}
              successAction={message.type == 'success' ? message.action : undefined}
              successIcon={message.type == 'success' ? message.icon : undefined}
              progressMessage={message.type == 'progress' ? message.content : null}
            />
            <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={pageLoading}>
              <CircularProgress color="inherit" />
            </Backdrop>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="/dashboard" element={ifLoggedIn(<DashboardPage />)} />
              <Route path="/organisations" element={ifLoggedIn(<OrganisationPage />)} />
              <Route path="/user-settings" element={ifLoggedIn(<UserSettingsPage />)} />
              <Route path="/emails" element={ifLoggedIn(<EmailsPage />)} />
              <Route path="/xpm" element={ifLoggedIn(<XpmPage />)} />
              <Route path="/firm" element={ifLoggedIn(<FirmPage />)} />
              <Route path="/manage" element={currentUser?.isAdmin || AppConfig.isLocal ? ifLoggedIn(<ManagePanel />) : null} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/subscription-confirmation" element={<SubscriptionConfirmation />} />
              <Route path="/logged-out" element={<LoggedOut />} />
              <Route path="/login" element={<XeroSso mode="login" />} />
              <Route path="/signup" element={<XeroSso mode="sign-up" />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/error" element={<ErrorPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </UserContext.Provider>
      </MessageContext.Provider>
    </div>
  );
};

export default Application;
